import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { MotionConfig } from 'framer-motion';
import {
  lightPalette,
  darkPalette,
  typography,
  components,
} from 'theme';

import { router } from './router';
import './i18n/setup';

import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/plus-jakarta-sans/800.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

// v7-style theme: cssVariables + colorSchemes is the recommended replacement
// for CssVarsProvider + extendTheme. Works in MUI 6.5+ and forward-compatible
// with v7.
const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme',
  },
  colorSchemes: {
    // The package palettes are MUI v6/v7-compatible PaletteOptions.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    light: { palette: lightPalette as any },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dark: { palette: darkPalette as any },
  },
  typography,
  // `components` is typed against the theme package's nested @mui/material
  // install. yarn workspaces with `file:..` deps create a duplicate MUI install
  // in node_modules/theme/node_modules/@mui, so the two Theme types are not
  // structurally identical. Cast at the boundary — the runtime shape is fine.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components: components as any,
  shape: { borderRadius: 12 },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      {/* FOUC prevention: see inline <script> in index.html which sets
          data-mui-color-scheme on <html> BEFORE first paint. The React-rendered
          <InitColorSchemeScript /> runs too late (post-hydration) on hard reloads. */}
      <ThemeProvider theme={theme} defaultMode="system">
        <CssBaseline />
        {/* Honor OS-level `prefers-reduced-motion`: when the user has reduced
            motion on, framer-motion zeroes-out transition durations app-wide. */}
        <MotionConfig reducedMotion="user">
          <RouterProvider router={router} />
        </MotionConfig>
      </ThemeProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
